/* eslint-disable no-console */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { useCallback, useEffect, useMemo, useState } from "react";
import useSWR from "swr";
import { useShallow } from "zustand/react/shallow";

import {
  receiveAllLADSDevices,
  receiveLADSDevice,
  receiveLADSFunctionalUnits,
} from "../api/lads";
import { useAccessToken } from "../auth-provider";
import {
  mapDeviceFunctionSetToViewUnit,
  mapLADSDevicesToViewDevices,
  mapLADSDeviceToViewDevice,
  mapLADSFunctionalUnitsToViewUnits,
} from "../mapper/lads-mapper";
import { useDevicesStore } from "../store/devices-store";
import { useUnitStore } from "../store/unit-store.ts";
import type { Device } from "../view-model";
import { useActiveRoom } from "./use-rooms";
import { useLADSDeviceChanged } from "./use-websocket-lads";

/** Compute relative position: device.hierarchicalLocation minus room prefix, formatted nicely */
function computeRelativePosition(
  device: Device,
  roomId?: string,
  roomName?: string
): void {
  if (!roomId) return;
  const loc = device.location.room || "";
  if (loc.toLowerCase().startsWith(roomId.toLowerCase())) {
    let relative = loc.slice(roomId.length);
    if (relative.startsWith("/")) relative = relative.slice(1);
    const formatSegment = (s: string) => s.replaceAll("_", " ");
    const parts = relative
      ? relative.split("/").map((segment) => formatSegment(segment))
      : [];
    if (roomName && parts.length > 0) {
      device.location.position = `${roomName} > ${parts.join(" > ")}`;
    } else if (roomName) {
      device.location.position = roomName;
    } else {
      device.location.position = parts.join(" > ");
    }
  }
}

// Helper function to receive and map LADS devices
const receiveDevices = async (
  activeRoom?: string,
  roomName?: string,
  accessToken?: string
): Promise<Device[]> => {
  const ladsDevices = await receiveAllLADSDevices(activeRoom, accessToken);
  const devices = mapLADSDevicesToViewDevices(ladsDevices);
  for (const d of devices) computeRelativePosition(d, activeRoom, roomName);
  return devices;
};

export const useDevices = () => {
  const { setDevices } = useDevicesStore();
  // Subscribe to all devices for reactivity (useShallow prevents infinite loops from Object.values)
  const allDevices = useDevicesStore(
    useShallow((state) => Object.values(state.devices))
  );
  const { activeRoom } = useActiveRoom();
  const accessToken = useAccessToken();

  const { data, error, isLoading, mutate } = useSWR(
    ["receiveAllLADSDevices", activeRoom, accessToken || undefined],
    ([, room, token]) => receiveDevices(room?.id, room?.name, token),
    {
      revalidateOnFocus: false,
      refreshInterval: 45_000, // Poll every 45 seconds
    }
  );

  // Sync SWR data to Zustand store
  useEffect(() => {
    if (data) {
      setDevices(data);
    }
  }, [data, setDevices]);

  // Subscribe to unified device change events via WebSocket
  const roomId = activeRoom?.id || import.meta.env.VITE_ROOM_ID || "";
  const handleDeviceAdded = useCallback(() => {
    // Re-fetch devices when a new device is added
    mutate();
  }, [mutate]);

  useLADSDeviceChanged(
    roomId,
    {
      onDeviceAdded: handleDeviceAdded,
      // status, removed, metadata changes are handled automatically by the hook
    },
    !!roomId
  );

  // Filter devices by active room
  // Only show devices that have a hierarchicalLocation (location.room) set
  const filteredDevices = useMemo(() => {
    // First, filter out devices without a location - they should only appear in admin page
    const devicesWithLocation = allDevices.filter((d) =>
      d.location?.room?.trim()
    );

    if (!activeRoom) return devicesWithLocation;
    const roomIdLc = activeRoom.id?.toLowerCase();
    return devicesWithLocation.filter((d) => {
      if (activeRoom.deviceIds?.includes(d.id)) return true;
      const deviceRoomLc = d.location.room?.toLowerCase();
      return deviceRoomLc && roomIdLc && deviceRoomLc.startsWith(roomIdLc);
    });
  }, [allDevices, activeRoom]);

  return {
    devices: filteredDevices,
    error,
    isLoading,
    mutate,
  };
};

// Simplified hooks that use Zustand directly
export const useFumehoods = () => {
  const { getDevicesByClass, setDevices } = useDevicesStore();
  const { activeRoom } = useActiveRoom();
  const accessToken = useAccessToken();

  // Fetch devices if not already loaded
  const { data, error, isLoading } = useSWR(
    ["receiveAllLADSDevices", activeRoom, accessToken || undefined],
    ([, room, token]) => receiveDevices(room?.id, room?.name, token),
    {
      revalidateOnFocus: false,
      refreshInterval: 45_000, // Poll every 45 seconds
    }
  );

  // Sync SWR data to Zustand store
  useEffect(() => {
    if (data) {
      setDevices(data);
    }
  }, [data, setDevices]);

  const allDevices = getDevicesByClass("workplace");

  const devices = useMemo(() => {
    // First, filter out devices without a location
    const devicesWithLocation = allDevices.filter((d) =>
      d.location?.room?.trim()
    );

    if (!activeRoom) return devicesWithLocation;
    const roomIdLc = activeRoom.id?.toLowerCase();
    return devicesWithLocation.filter((d) => {
      if (activeRoom.deviceIds?.includes(d.id)) return true;
      const deviceRoomLc = d.location.room?.toLowerCase();
      return deviceRoomLc && roomIdLc && deviceRoomLc.startsWith(roomIdLc);
    });
  }, [allDevices, activeRoom]);

  return {
    devices,
    error,
    isLoading,
  };
};

export const useMonitors = () => {
  const { getDevicesByClass } = useDevicesStore();
  const devices = getDevicesByClass("monitoring");

  return {
    devices,
    error: undefined,
    isLoading: false,
  };
};

export const useAllDevices = () => {
  const { getDevices, setDevices } = useDevicesStore();
  const { activeRoom } = useActiveRoom();
  const accessToken = useAccessToken();

  const { data, error, isLoading } = useSWR(
    ["receiveAllLADSDevices", activeRoom, accessToken || undefined],
    ([, room, token]) => room && receiveDevices(room.id, room.name, token),
    {
      revalidateOnFocus: false,
      refreshInterval: 45_000, // Poll every 45 seconds
    }
  );

  // Sync SWR data to Zustand store
  useEffect(() => {
    if (data) {
      setDevices(data);
    }
  }, [data, setDevices]);

  const allDevices = getDevices().filter((d) => d.class !== "monitoring");

  const devices = useMemo(() => {
    // First, filter out devices without a location
    const devicesWithLocation = allDevices.filter((d) =>
      d.location?.room?.trim()
    );

    if (!activeRoom) return devicesWithLocation;
    const roomIdLc = activeRoom.id?.toLowerCase();
    return devicesWithLocation.filter((d) => {
      if (activeRoom.deviceIds?.includes(d.id)) return true;
      const deviceRoomLc = d.location.room?.toLowerCase();
      return deviceRoomLc && roomIdLc && deviceRoomLc.startsWith(roomIdLc);
    });
  }, [allDevices, activeRoom]);

  return {
    devices,
    error,
    isLoading,
  };
};

export const useDetailDevice = (id?: string) => {
  const { updateDevice } = useDevicesStore();
  // Subscribe to specific device changes for reactivity
  const device = useDevicesStore((state) =>
    id ? state.devices[id] : undefined
  );
  const accessToken = useAccessToken();
  const { activeRoom } = useActiveRoom();

  // Fetch device on mount — uses ID directly so it works on page refresh
  // (store is empty after F5, so we can't depend on device being in the store)
  const { isLoading: isLoadingDevices } = useSWR(
    id ? ["receiveLADSDevice", id, accessToken || undefined] : null,
    ([, deviceId, token]) =>
      receiveLADSDevice(deviceId, token).then((ladsDevice) => {
        const mappedDevice = mapLADSDeviceToViewDevice(ladsDevice);
        computeRelativePosition(mappedDevice, activeRoom?.id, activeRoom?.name);
        return mappedDevice;
      }),
    {
      revalidateOnFocus: false,
      refreshInterval: 45_000,
      onSuccess: (data) => {
        if (id && data) {
          updateDevice(id, data);
        }
      },
    }
  );

  // FunctionalUnits are fetched by useUnits in DeviceDetail — no need to duplicate here

  if (!id) {
    return {
      device: undefined,
      error: new Error("Device ID is required"),
      isLoading: false,
    };
  }

  // Only wait for device fetch — FU loading is handled by useUnits in DeviceDetail
  const combinedLoading = isLoadingDevices;

  // Don't show "Device not found" error while devices are still loading
  const combinedError =
    !combinedLoading && !device ? new Error("Device not found") : null;

  return {
    device,
    error: combinedError,
    isLoading: combinedLoading,
  };
};

export const useUnits = (deviceId: string) => {
  const { addUnits, getUnitsForDevice } = useUnitStore();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const accessToken = useAccessToken();
  const [fetchKey, setFetchKey] = useState(0);

  const fetchUnits = useCallback(() => {
    if (!deviceId) return;

    setIsLoading(true);
    setError(null);

    receiveLADSFunctionalUnits(deviceId, accessToken || undefined)
      .then(async (functionalUnits) => {
        const receivedUnits = mapLADSFunctionalUnitsToViewUnits(
          functionalUnits,
          deviceId
        );
        // Monitored devices (LCU-875) have no FunctionalUnits — their
        // functions live in the device-level functionSet. Only then is the
        // extra device fetch worth it.
        if (receivedUnits.length === 0) {
          const ladsDevice = await receiveLADSDevice(
            deviceId,
            accessToken || undefined
          );
          if (ladsDevice.functionSet?.length) {
            receivedUnits.push(
              mapDeviceFunctionSetToViewUnit(ladsDevice, deviceId)
            );
          }
        }
        addUnits(receivedUnits);
        setIsLoading(false);
      })
      .catch((error_) => {
        setError(error_ instanceof Error ? error_ : new Error(String(error_)));
        setIsLoading(false);
      });
  }, [addUnits, deviceId, accessToken]);

  const refetch = useCallback(() => {
    console.log(`[useUnits] Refetching FUs for device ${deviceId}`);
    setFetchKey((k) => k + 1);
  }, [deviceId]);

  useEffect(() => {
    fetchUnits();
  }, [fetchUnits, fetchKey]);

  if (!deviceId) {
    return {
      units: undefined,
      error: new Error("Device ID is required"),
      isLoading: false,
      refetch,
    };
  }

  const deviceUnits = getUnitsForDevice(deviceId);
  deviceUnits.sort((a) => {
    if (a.id === "Fumehood") return -1;
    if (a.id === "SensorPanel") return -1;
    return 0;
  });

  return {
    units: deviceUnits,
    error: error || (deviceUnits ? null : new Error("Device not found")),
    isLoading,
    refetch,
  };
};
